/**
 * ProvenanceContext — shared provenance state across all panels.
 *
 * Provides:
 * - Current project ID
 * - Current branch (defaults to "main")
 * - Multi-node selection for cross-panel linking (DAG ↔ Data Explorer ↔ Changelog)
 */

import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";

/** Which secondary panel is open in the slide panel, or null for DAG-only. */
export type PanelTab =
  | "dag" | "data" | "plot"
  | "saved-plots" | "detail" | "viewer" | "cockpit"
  | "config" | "repro" | "history" | "conversations"
  | "geometry" | "campaign"
  | null;

const VALID_PANELS = new Set<string>([
  "dag", "data", "plot",
  "saved-plots", "detail", "viewer", "cockpit",
  "config", "repro", "history", "conversations",
  "geometry", "campaign",
]);

export interface ProvenanceContextValue {
  projectId: string;
  currentBranch: string;
  setBranch: (branch: string) => void;
  /** Node UUIDs currently selected across all panels. */
  selectedNodeIds: Set<string>;
  /** Replace the entire selection with the given node IDs. */
  selectNodes: (ids: string[]) => void;
  /** Clear all selected nodes. */
  clearSelection: () => void;
  /** Backward-compat: first selected node ID, or null. */
  selectedArtifactId: string | null;
  /** Backward-compat alias for selectNodes([id]) / clearSelection(). */
  selectArtifact: (id: string | null) => void;
  /** Which secondary panel tab is currently open (null = DAG-only). */
  openPanel: PanelTab;
  setOpenPanel: (tab: PanelTab) => void;
  /** Atomically select a node AND open a panel in a single URL update. */
  selectArtifactAndOpenPanel: (nodeId: string, panel: PanelTab, extraParams?: Record<string, string>) => void;
  /** Seeded x/y/z fields for Plot Builder (set via correlogram "Open in Plot Builder"). */
  plotBuilderSeed: { xField: string; yField: string; zField?: string } | null;
  /** Switch to Plot Builder tab with pre-filled x/y (and optionally z) axes. */
  openPlotBuilder: (xField: string, yField: string, zField?: string) => void;
}

export const ProvenanceContext = createContext<ProvenanceContextValue | null>(
  null,
);

export function useProvenanceContext(): ProvenanceContextValue {
  const ctx = useContext(ProvenanceContext);
  if (!ctx) throw new Error("useProvenanceContext must be inside ProvenanceProvider");
  return ctx;
}

interface Props {
  projectId: string;
  branch?: string;
  children: ReactNode;
}

export function ProvenanceProvider({ projectId, branch: defaultBranch = "main", children }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [plotBuilderSeed, setPlotBuilderSeed] = useState<{ xField: string; yField: string; zField?: string } | null>(null);

  // -- Branch (already URL-synced) --
  const currentBranch = searchParams.get("branch") || defaultBranch;

  // -- Selected nodes: derived from URL ?nodes= param --
  const nodesParam = searchParams.get("nodes") || "";
  const selectedNodeIds = useMemo(() => {
    const ids = nodesParam ? nodesParam.split(",").filter(Boolean) : [];
    return new Set(ids);
  }, [nodesParam]);

  // Stable ref so callbacks don't re-create on every render
  const selectedRef = useRef(selectedNodeIds);
  selectedRef.current = selectedNodeIds;

  // -- Open panel: derived from URL ?panel= param --
  const panelParam = searchParams.get("panel");
  const openPanel: PanelTab = panelParam && VALID_PANELS.has(panelParam)
    ? (panelParam as PanelTab)
    : null;

  const _updateParams = useCallback(
    (updater: (prev: URLSearchParams) => URLSearchParams) => {
      setSearchParams(updater, { replace: true });
    },
    [setSearchParams],
  );

  const setBranch = useCallback(
    (branch: string) => {
      _updateParams((prev) => {
        const next = new URLSearchParams(prev);
        if (branch === defaultBranch) next.delete("branch");
        else next.set("branch", branch);
        return next;
      });
    },
    [_updateParams, defaultBranch],
  );

  const selectNodes = useCallback((ids: string[]) => {
    _updateParams((prev) => {
      const next = new URLSearchParams(prev);
      if (ids.length === 0) next.delete("nodes");
      else next.set("nodes", ids.join(","));
      return next;
    });
  }, [_updateParams]);

  const clearSelection = useCallback(() => {
    _updateParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("nodes");
      return next;
    });
  }, [_updateParams]);

  const selectedArtifactId = selectedNodeIds.size === 1
    ? ([...selectedNodeIds][0] ?? null)
    : null;

  const selectArtifact = useCallback((id: string | null) => {
    selectNodes(id ? [id] : []);
  }, [selectNodes]);

  const setOpenPanel = useCallback((tab: PanelTab) => {
    _updateParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === null) next.delete("panel");
      else next.set("panel", tab);
      return next;
    });
  }, [_updateParams]);

  const selectArtifactAndOpenPanel = useCallback(
    (nodeId: string, panel: PanelTab, extraParams?: Record<string, string>) => {
      _updateParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("nodes", nodeId);
        if (panel === null) next.delete("panel");
        else next.set("panel", panel);
        if (extraParams) {
          for (const [k, v] of Object.entries(extraParams)) next.set(k, v);
        }
        return next;
      });
    },
    [_updateParams],
  );

  const openPlotBuilder = useCallback((xField: string, yField: string, zField?: string) => {
    setPlotBuilderSeed({ xField, yField, zField });
    setOpenPanel("plot");
  }, [setOpenPanel]);

  const value = useMemo<ProvenanceContextValue>(() => ({
    projectId,
    currentBranch,
    setBranch,
    selectedNodeIds,
    selectNodes,
    clearSelection,
    selectedArtifactId,
    selectArtifact,
    openPanel,
    setOpenPanel,
    selectArtifactAndOpenPanel,
    plotBuilderSeed,
    openPlotBuilder,
  }), [
    projectId, currentBranch, setBranch,
    selectedNodeIds, selectNodes, clearSelection,
    selectedArtifactId, selectArtifact,
    openPanel, setOpenPanel, selectArtifactAndOpenPanel,
    plotBuilderSeed, openPlotBuilder,
  ]);

  return (
    <ProvenanceContext.Provider value={value}>
      {children}
    </ProvenanceContext.Provider>
  );
}
